import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect } from 'react-router-dom';

import { selectUser } from 'entities/user/selectors';
import { showMessageAction } from 'containers/Notification/actions';
import { Features } from 'utils/features';
import { Privileges } from 'utils/privileges';

interface HasFeaturesProps {
  component: React.ComponentType;
  hasFeatures: Features[];
  hasPrivileges: Privileges[];
  requireAll: boolean;
  ignoreFeatureFlag: boolean; // a workaround which allows to render a route even if it's restricted by feature flag
}

const HasFeatures = ({
  component: PureComponent,
  hasFeatures = [],
  hasPrivileges = [],
  requireAll = true,
  ignoreFeatureFlag = false,
  ...props
}: HasFeaturesProps): JSX.Element | null => {
  const user = useSelector(selectUser);
  const dispatch = useDispatch();
  const featureComparator: 'every' | 'some' = requireAll ? 'every' : 'some';

  if (!user) {
    return null;
  }

  if (
    (!ignoreFeatureFlag &&
      !hasFeatures?.[featureComparator]((feature) => user.hasFeature(feature))) ||
    !hasPrivileges?.[featureComparator]((privilege) => user.hasPrivilege(privilege))
  ) {
    dispatch(showMessageAction('This page is restricted'));
    return <Redirect to="/" />;
  }

  return <PureComponent {...props} />;
};

export default HasFeatures;
